/**
 * Price Card Component
 * Displays real-time price and CVD for an asset
 */

import { useMemo, useRef, useEffect, useState } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAlertStore, getFlatPrice, getNetCVD, type Asset } from '@/stores/alert-store';
import { formatPrice, formatCompact, cn } from '@/lib/utils';

interface PriceCardProps {
  asset: Asset;
  onClick?: () => void;
  selected?: boolean;
}

export function PriceCard({ asset, onClick, selected }: PriceCardProps) {
  const { prices, cvd } = useAlertStore();
  const [priceFlash, setPriceFlash] = useState<'up' | 'down' | null>(null);
  const prevPriceRef = useRef<number>(0);

  const assetPrices = prices[asset];
  const assetCVD = cvd[asset];

  const price = useMemo(() => getFlatPrice(assetPrices), [assetPrices]);
  const netCVD = useMemo(() => getNetCVD(assetCVD), [assetCVD]);

  // Flash effect on price change
  useEffect(() => {
    if (prevPriceRef.current !== 0 && price !== prevPriceRef.current) {
      setPriceFlash(price > prevPriceRef.current ? 'up' : 'down');
      const timeout = setTimeout(() => setPriceFlash(null), 300);
      return () => clearTimeout(timeout);
    }
    prevPriceRef.current = price;
  }, [price]);

  const cvdDirection = netCVD > 0 ? 'bullish' : netCVD < 0 ? 'bearish' : 'neutral';

  return (
    <Card
      className={cn(
        'cursor-pointer transition-all hover:border-primary/50',
        selected && 'border-primary ring-1 ring-primary',
        priceFlash === 'up' && 'price-up',
        priceFlash === 'down' && 'price-down'
      )}
      onClick={onClick}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-bold">{asset}</CardTitle>
          <Badge variant={cvdDirection === 'bullish' ? 'bullish' : cvdDirection === 'bearish' ? 'bearish' : 'secondary'}>
            {cvdDirection === 'bullish' ? (
              <TrendingUp className="mr-1 h-3 w-3" />
            ) : cvdDirection === 'bearish' ? (
              <TrendingDown className="mr-1 h-3 w-3" />
            ) : null}
            CVD
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {/* Price */}
          <div className="text-2xl font-bold tabular-nums">
            {price > 0 ? formatPrice(price) : '—'}
          </div>

          {/* CVD */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Net CVD</span>
            <span
              className={cn(
                'tabular-nums font-medium',
                netCVD > 0 ? 'text-green-500' : netCVD < 0 ? 'text-red-500' : 'text-muted-foreground'
              )}
            >
              {netCVD > 0 ? '+' : ''}
              {formatCompact(netCVD)}
            </span>
          </div>

          {/* Spot/Perp breakdown */}
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Spot: {formatCompact(assetCVD?.spotTotal?.net || 0)}
            </span>
            <span>
              Perp: {formatCompact(assetCVD?.perpTotal?.net || 0)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
